<template>
  <header>
    <div class="page-title">
      <h3>{{ reportsTitle }}</h3>
      <span class="tw-flex tw-items-center">
        <IconCalendarBlankFill class="icon" id="calendar" />{{ dateRangeFrom }} – {{ dateRangeTo }}
      </span>
    </div>

    <div class="tw-flex tw-gap-2">
      <SPAButton id="AdjustFilterBtn" @click.prevent="handleClick" color="success"><IconFunnelSimple class="icon" id="funnel"/>Adjust Filter</SPAButton>
      <SPAButton id="ExportReportBtn" @click.prevent="toggleReportModal" :customOutline="isExportReady ? 'default' : 'disabled'"><IconDownload class="icon" id="download" />Export Report</SPAButton>
    </div>
  </header>
  <main id="ProjectTaggingMainReport">
    <ProjectTaggingMainReportTable />
    <ProjectTaggingReportDrawer />
    <ProjectTaggingReportSelectEmployee />
    <ProjectTaggingReportSelectProject />
  </main>
  <SPAModal id="ExportReport" staticBackdrop v-model="exportReportModalOpen" @hide="exportReportModalOpen = false">
    <template #modal-title>
      <span>Download File</span>
      <p>
        Are you sure you want to download the report named <strong>{{ downloadFileName }}</strong> with the summary below:
      </p>
    </template>
    <template #modal-body>
      <div class="selected-project-info">
        <span>Date Range</span>
        <p>{{ dateRangeFrom }} – {{ dateRangeTo }}</p>
      </div>
      <div class="selected-project-info">
        <span>Project</span>
        <p>{{ confirmedProject?.name }}</p>
      </div>
      <div class="selected-project-info">
        <span>Employees</span>
        <p>{{ showEmployees }}</p>
      </div>
    </template>
    <template #modal-footer>
      <SPAButton color="light" @click="exportReportModalOpen = false">Cancel</SPAButton>
      <SPAButton color="success" @click="exportReport">Yes, Download File</SPAButton>
    </template>
  </SPAModal>
  <SPADownloadLoader :isOpen="exportReportLoading" />
</template>

<script>
import { storeToRefs } from 'pinia';
import IconFunnelSimple from '~icons/ph/funnel-simple';
import IconDownload from '~icons/ph/download';
import IconCalendarBlankFill from '~icons/ph/calendar-blank-fill';
import ProjectTaggingReportDrawer from './ProjectTaggingReportDrawer/ProjectTaggingReportDrawer.vue';
import { useProjectTaggingStore } from '../../../../stores/projectTagging';
import SPAButton from '../../../../components/SPAButton.vue';
import ProjectTaggingMainReportTable from './ProjectTaggingMainReportTable/ProjectTaggingMainReportTable.vue';
import ProjectTaggingReportSelectEmployee from './ProjectTaggingReportSelectEmployee.vue';
import ProjectTaggingReportSelectProject from './ProjectTaggingReportSelectProject.vue';
import { parseDate, fileNameDateFormat } from '../../../../helpers/dateParser';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import SPAModal from '../../../../components/SPAModal.vue';
import SPADownloadLoader from '../../../../components/SPADownloadLoader.vue';
import { PostReport } from '../../../../services/Reports/PostReport';
import { ReportTypesEnum } from '../../../../enums/reportTypes';
import { useReportStore } from '../../../../stores/report';

export default {
  components: {
    IconFunnelSimple,
    IconDownload,
    IconCalendarBlankFill,
    ProjectTaggingReportDrawer,
    SPAButton,
    ProjectTaggingMainReportTable,
    ProjectTaggingReportSelectEmployee,
    ProjectTaggingReportSelectProject,
    SPAModal,
    SPADownloadLoader,
  },
  setup() {
    const projectTagging = useProjectTaggingStore();
    const reportStore = useReportStore();
    const { drawerReportState, reportsHeaders, reportsTitle, confirmedProject, confirmedEmployees, viewEmployeeListDrawer, viewProjectListDrawer, showEmployees, isFiltersUpdated } =
      storeToRefs(projectTagging);

    const handleClick = () => {
      drawerReportState.value = !drawerReportState.value;
    };
    const dateRangeFrom = computed(() => (reportsHeaders.value.length > 0 ? parseDate(reportsHeaders.value[0]) : ''));
    const dateRangeTo = computed(() => (reportsHeaders.value.length > 0 ? parseDate(reportsHeaders.value[reportsHeaders.value.length - 1]) : ''));
    const isExportReady = computed(() => confirmedProject.value && dateRangeFrom.value != '' && dateRangeTo.value != '' && !isFiltersUpdated.value);
    const exportReportModalOpen = ref(false);
    const exportReportLoading = ref(false);
    const toggleReportModal = () => {
      if (!isExportReady.value) return;
      exportReportModalOpen.value = true;
    };
    const downloadFileName = computed(() => {
      return `Plogs-${fileNameDateFormat(dateRangeFrom.value)}-${fileNameDateFormat(dateRangeTo.value)}.xlsx`;
    });
    const employeeIds = (employees) => {
      let arrayOfIds = [];
      if (employees.length > 0) {
        employees.forEach((employee) => {
          arrayOfIds = [...arrayOfIds, employee.employeeId];
        });
      }
      return arrayOfIds;
    };
    const exportReport = async () => {
      if (!isExportReady.value) return;
      exportReportLoading.value = true;
      exportReportModalOpen.value = false;

      await PostReport({
        DateFrom: dateRangeFrom.value,
        DateTo: dateRangeTo.value,
        ReportType: ReportTypesEnum.projectTagging,
        OtherData: {
          ProjectId: confirmedProject.value?.id,
          EmployeeIds: employeeIds(confirmedEmployees.value),
        },
      });
      exportReportLoading.value = false;
      reportStore.toggleInfoToast();
    };
    onMounted(() => {
      drawerReportState.value = true;
      viewEmployeeListDrawer.value = false;
      viewProjectListDrawer.value = false;
    });
    onUnmounted(() => {
      drawerReportState.value = false;
      confirmedProject.value = {};
      confirmedEmployees.value = [];
      reportsTitle.value = 'No Project Selected';
      dateRangeFrom.value = '';
      dateRangeTo.value = '';
      viewEmployeeListDrawer.value = false;
      viewProjectListDrawer.value = false;
    });
    return {
      handleClick,
      reportsHeaders,
      reportsTitle,
      dateRangeFrom,
      dateRangeTo,
      isExportReady,
      exportReportModalOpen,
      exportReport,
      confirmedProject,
      showEmployees,
      exportReportLoading,
      toggleReportModal,
      downloadFileName,
    };
  },
};
</script>

<style lang="scss" scoped>
header {
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-bottom: 32px;
  .page-title {
    color: $neutral-800;
    h3 {
      font-weight: 500;
      font-size: 24px;
      line-height: 36px;
      margin-bottom: 0;
    }
  }
  .icon {
    margin-right: 9px;
    &#download,
    &#funnel,
    &#calendar {
      height: 14px;
    }
  }
  #calendar {
    color: $green-300;
  }
  #AdjustFilterBtn {
    margin-left: auto;
    border: 1px solid $green-300;
  }
}
/* Drawer */
:deep(.sidenav) {
  padding-top: 78px;
  z-index: auto;
  width: 354px;
  box-shadow: 0px -4px 8px rgba(60, 91, 81, 0.16);
  .ps {
    height: 100% !important;
  }
}
#ExportReport {
  font-size: 16px;
  line-height: 24px;
  color: $neutral-800;
  p {
    margin-bottom: 0;
  }
  .modal-title span {
    font-weight: 500;
    margin-bottom: 8px;
  }
  .modal-title p {
    margin-top: 8px;
  }
  .selected-project-info {
    padding-inline: 8px;

    &:not(:last-child) {
      margin-bottom: 20px;
    }
    span {
      font-size: 12px;
      line-height: 20px;
      color: $neutral-80;
    }
  }
}
</style>
